/**
 * SearchBar — reusable sticky search input.
 *
 * Extracted from PoliciesPage so the same UX (sticky bar, "/" or
 * Cmd/Ctrl+K shortcut, count badge, clear button) is available on
 * Controls, Evidence, CAIQ — and anywhere else we add later.
 *
 * Usage:
 *   const [q, setQ] = useState('');
 *   <SearchBar
 *     value={q}
 *     onChange={setQ}
 *     placeholder="Find a control…"
 *     totalCount={controls.length}
 *     filteredCount={filtered.length}
 *     testIdPrefix="control-search"
 *   />
 */
import React, { useEffect, useRef } from 'react';
import { Search, X } from 'lucide-react';

export default function SearchBar({
  value,
  onChange,
  placeholder = 'Search…',
  totalCount,
  filteredCount,
  testIdPrefix = 'search',
  enableShortcut = true,
}) {
  const inputRef = useRef(null);

  useEffect(() => {
    if (!enableShortcut) return undefined;
    const onKeyDown = (e) => {
      const tag = (e.target?.tagName || '').toLowerCase();
      const isTyping = tag === 'input' || tag === 'textarea' || e.target?.isContentEditable;
      if (!isTyping && e.key === '/') {
        e.preventDefault();
        inputRef.current?.focus();
      } else if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
      } else if (e.key === 'Escape' && document.activeElement === inputRef.current) {
        onChange('');
        inputRef.current?.blur();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [enableShortcut, onChange]);

  const showCount =
    typeof totalCount === 'number' && typeof filteredCount === 'number';

  return (
    <div
      className="page-search-bar"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        background: 'rgba(255,255,255,0.04)',
        border: '1px solid rgba(255,255,255,0.10)',
        borderRadius: 10,
        padding: '10px 14px',
        marginBottom: 16,
        position: 'sticky',
        top: 0,
        zIndex: 5,
      }}
      data-testid={`${testIdPrefix}-bar`}
    >
      <Search size={18} style={{ color: '#94a3b8', flexShrink: 0 }} />
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={`${testIdPrefix}-input`}
        aria-label={placeholder}
        style={{
          flex: 1,
          background: 'transparent',
          border: 'none',
          outline: 'none',
          color: 'inherit',
          fontSize: 15,
        }}
      />
      {value && (
        <button
          type="button"
          onClick={() => { onChange(''); inputRef.current?.focus(); }}
          data-testid={`${testIdPrefix}-clear`}
          title="Clear search (Esc)"
          style={{
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            color: '#94a3b8',
            padding: 4,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <X size={16} />
        </button>
      )}
      <span
        style={{
          fontSize: 12,
          color: '#6b7280',
          padding: '2px 8px',
          border: '1px solid rgba(255,255,255,0.12)',
          borderRadius: 4,
          fontFamily: 'ui-monospace, monospace',
          flexShrink: 0,
        }}
        title="Press / or Cmd/Ctrl+K to focus"
        data-testid={`${testIdPrefix}-count`}
      >
        {value && showCount
          ? `${filteredCount} of ${totalCount}`
          : '/ or ⌘K'}
      </span>
    </div>
  );
}
